import React from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {Table} from '@/web-components';
import {
	CUSTOM_FIELD_PREFIX,
	getCustomFieldColumnName,
} from '../../../forecast-app/project-tab/projects/scoping-page/ProjectScopingUtil';
import Util from '../../../forecast-app/shared/util/util';
import {hasFeatureFlag} from '../../../forecast-app/shared/util/FeatureUtil';
import {TaskBreakdownHeadersTooltips as Tooltips} from './TaskBreakdownHeadersTooltips';

const getColumnsWithCategoriesFF = columns => {
	return [
		<Table.HeaderColumn usePadding align="right" visible={columns['hours.estimated']} width="MEDIUM">
			<Tooltips.EstimatedHoursTooltip size={'m'}>
				<Table.HeaderColumn.Title>
					<FormattedMessage id="common.estimated_hours" />
				</Table.HeaderColumn.Title>
			</Tooltips.EstimatedHoursTooltip>
		</Table.HeaderColumn>,
		<Table.HeaderColumn usePadding align="right" visible={columns['hours.reportedTime']} width="MEDIUM">
			<Tooltips.ActualHoursTooltip size={'m'}>
				<Table.HeaderColumn.Title>
					<FormattedMessage id="common.actual_hours" />
				</Table.HeaderColumn.Title>
			</Tooltips.ActualHoursTooltip>
		</Table.HeaderColumn>,
		<Table.HeaderColumn usePadding align="right" visible={columns['hours.reportedBillableTime']} width="MEDIUM">
			<Tooltips.ActualBillableHoursTooltip size={'m'}>
				<Table.HeaderColumn.Title>
					<FormattedMessage id="common.actual_billable_hours" />
				</Table.HeaderColumn.Title>
			</Tooltips.ActualBillableHoursTooltip>
		</Table.HeaderColumn>,
		<Table.HeaderColumn usePadding align="right" visible={columns['hours.reportedNonBillableTime']} width="MEDIUM">
			<Tooltips.ActualNonBillableHoursTooltip size={'m'}>
				<Table.HeaderColumn.Title>
					<FormattedMessage id="common.actual_non_billable_hours" />
				</Table.HeaderColumn.Title>
			</Tooltips.ActualNonBillableHoursTooltip>
		</Table.HeaderColumn>,
		<Table.HeaderColumn align="right" usePadding visible={columns['hours.remaining']} width="MEDIUM">
			<Tooltips.RemainingWorkHoursTooltip size={'m'}>
				<Table.HeaderColumn.Title>
					<FormattedMessage id="common.remaining_work_hours" />
				</Table.HeaderColumn.Title>
			</Tooltips.RemainingWorkHoursTooltip>
		</Table.HeaderColumn>,
		<Table.HeaderColumn usePadding align="right" visible={columns['hours.projected']} width="MEDIUM">
			<Tooltips.ProjectedTotalHoursTooltip size={'l'}>
				<Table.HeaderColumn.Title>
					<FormattedMessage id="common.projected_total_hours" />
				</Table.HeaderColumn.Title>
			</Tooltips.ProjectedTotalHoursTooltip>
		</Table.HeaderColumn>,
		<Table.HeaderColumn usePadding align="right" visible={columns['points.estimated']} width="MEDIUM">
			<Tooltips.EstimatedPointsTooltip size={'m'}>
				<Table.HeaderColumn.Title>
					<FormattedMessage id="common.estimated_points" />
				</Table.HeaderColumn.Title>
			</Tooltips.EstimatedPointsTooltip>
		</Table.HeaderColumn>,
		<Table.HeaderColumn align="right" usePadding visible={columns['points.remaining']} width="MEDIUM">
			<Tooltips.RemainingWorkPointsTooltip size={'m'}>
				<Table.HeaderColumn.Title>
					<FormattedMessage id="common.remaining_work_points" />
				</Table.HeaderColumn.Title>
			</Tooltips.RemainingWorkPointsTooltip>
		</Table.HeaderColumn>,
	];
};

const getColumnsWithoutCategoriesFF = columns => {
	return [
		<Table.HeaderColumn usePadding align="right" visible={columns['work.estimate.hours']} width="MEDIUM">
			<Table.HeaderColumn.Title>
				<FormattedMessage id="common.estimate" />
				<br />
				<FormattedMessage id="common.hours" />
			</Table.HeaderColumn.Title>
		</Table.HeaderColumn>,
		<Table.HeaderColumn usePadding align="right" visible={columns['work.estimate.points']} width="MEDIUM">
			<Table.HeaderColumn.Title>
				<FormattedMessage id="common.estimate" />
				<br />
				<FormattedMessage id="common.points" />
			</Table.HeaderColumn.Title>
		</Table.HeaderColumn>,
		<Table.HeaderColumn usePadding align="right" visible={columns['reported.projected']} width="MEDIUM">
			<Table.HeaderColumn.Title>
				<FormattedMessage id="task.total_time_at_completion" />
			</Table.HeaderColumn.Title>
		</Table.HeaderColumn>,
		<Table.HeaderColumn usePadding align="right" visible={columns['reported.reportedTime']} width="MEDIUM">
			<Table.HeaderColumn.Title>
				<FormattedMessage id="common.time_entries" />
			</Table.HeaderColumn.Title>
		</Table.HeaderColumn>,
		<Table.HeaderColumn usePadding align="right" visible={columns['reported.reportedBillableTime']} width="MEDIUM">
			<Table.HeaderColumn.Title>
				<FormattedMessage id="utilization.billable_time" />
			</Table.HeaderColumn.Title>
		</Table.HeaderColumn>,
		<Table.HeaderColumn usePadding align="right" visible={columns['reported.reportedNonBillableTime']} width="MEDIUM">
			<Table.HeaderColumn.Title>
				<FormattedMessage id="utilization.non_billable_time" />
			</Table.HeaderColumn.Title>
		</Table.HeaderColumn>,
		<Table.HeaderColumn align="right" usePadding visible={columns['work.remaining.hours']} width="MEDIUM">
			<Table.HeaderColumn.Title>
				<FormattedMessage id="common.remaining" />
				<br />
				<FormattedMessage id="common.hours" />
			</Table.HeaderColumn.Title>
		</Table.HeaderColumn>,
		<Table.HeaderColumn align="right" usePadding visible={columns['work.remaining.points']} width="MEDIUM">
			<Table.HeaderColumn.Title>
				<FormattedMessage id="common.remaining" />
				<br />
				<FormattedMessage id="common.points" />
			</Table.HeaderColumn.Title>
		</Table.HeaderColumn>,
	];
};

type BaseLevelTableHeaderColumns = {[key: string]: boolean};
type BaseLevelTableHeaderCustomFieldDefinitions = Array<{key: string; displayName: string}>;

export const getBaseLevelTableHeader = (
	columns: BaseLevelTableHeaderColumns,
	customFieldDefinitions: BaseLevelTableHeaderCustomFieldDefinitions
): React.JSX.Element => {
	const {formatMessage} = useIntl();
	const customFields = Util.hasCustomFields()
		? customFieldDefinitions
				.map(cfd => {
					const key = Object.keys(columns).find(
						key => key === CUSTOM_FIELD_PREFIX + '.' + getCustomFieldColumnName('TASK', cfd.key)
					);
					return {
						displayName: cfd.displayName,
						visible: key ? columns[key] : false,
					};
				})
				.sort((a, b) => (a.displayName > b.displayName ? 1 : -1))
		: [];

	const hasFinancialCategoriesUpdate = hasFeatureFlag('financial_categories_update');

	return (
		<Table.Header>
			<Table.HeaderColumn usePadding flex={1} align="left" visible={true} width="LARGE">
				<Table.HeaderColumn.Title>&nbsp;</Table.HeaderColumn.Title>
			</Table.HeaderColumn>
			<Table.HeaderColumn align="left" usePadding visible={columns.indicators} width="MEDIUM">
				<Table.HeaderColumn.Title>{formatMessage({id: 'common.indicators'})}</Table.HeaderColumn.Title>
			</Table.HeaderColumn>
			<Table.HeaderColumn usePadding align="left" visible={columns.project} width="MEDIUM">
				<Table.HeaderColumn.Title>{formatMessage({id: 'common.project'})}</Table.HeaderColumn.Title>
			</Table.HeaderColumn>
			<Table.HeaderColumn align="left" usePadding visible={columns.projectDeadline} width="MEDIUM">
				<Table.HeaderColumn.Title>
					{formatMessage({id: 'insights.component.list.column.projectDeadline'})}
				</Table.HeaderColumn.Title>
			</Table.HeaderColumn>
			<Table.HeaderColumn align="left" usePadding visible={columns.projectStage} width="MEDIUM">
				<Table.HeaderColumn.Title>
					{formatMessage({id: 'insights.component.list.column.projectStage'})}
				</Table.HeaderColumn.Title>
			</Table.HeaderColumn>
			<Table.HeaderColumn align="left" usePadding visible={columns.rateCard} width="MEDIUM">
				<Table.HeaderColumn.Title>{formatMessage({id: 'common.rate-card'})}</Table.HeaderColumn.Title>
			</Table.HeaderColumn>
			<Table.HeaderColumn align="left" usePadding visible={columns.clientName} width="SMALL">
				<Table.HeaderColumn.Title>{formatMessage({id: 'common.client'})}</Table.HeaderColumn.Title>
			</Table.HeaderColumn>
			<Table.HeaderColumn align="left" usePadding visible={columns['taskDates.startDate']} width="MEDIUM">
				<Table.HeaderColumn.Title>
					{formatMessage({id: 'common.task'})} {formatMessage({id: 'common.start_date'})}
				</Table.HeaderColumn.Title>
			</Table.HeaderColumn>
			<Table.HeaderColumn align="left" usePadding visible={columns['taskDates.deadline']} width="MEDIUM">
				<Table.HeaderColumn.Title>
					{formatMessage({id: 'common.task'})} {formatMessage({id: 'common.end_date'})}
				</Table.HeaderColumn.Title>
			</Table.HeaderColumn>
			<Table.HeaderColumn align="left" usePadding visible={columns.roleName} width="MEDIUM">
				<Table.HeaderColumn.Title>{formatMessage({id: 'common.role'})}</Table.HeaderColumn.Title>
			</Table.HeaderColumn>
			<Table.HeaderColumn align="left" usePadding visible={columns.assignees} width="LARGE">
				<Table.HeaderColumn.Title>{formatMessage({id: 'common.assignees'})}</Table.HeaderColumn.Title>
			</Table.HeaderColumn>
			<Table.HeaderColumn align="left" usePadding visible={columns.status} width="MEDIUM">
				<Table.HeaderColumn.Title>{formatMessage({id: 'common.status'})}</Table.HeaderColumn.Title>
			</Table.HeaderColumn>
			<Table.HeaderColumn align="left" usePadding visible={columns.phase} width="MEDIUM">
				<Table.HeaderColumn.Title>{formatMessage({id: 'common.phase'})}</Table.HeaderColumn.Title>
			</Table.HeaderColumn>
			<Table.HeaderColumn align="left" usePadding visible={columns['phaseDates.startDate']} width="MEDIUM">
				<Table.HeaderColumn.Title>
					{formatMessage({id: 'common.phase'})} {formatMessage({id: 'common.start_date'})}
				</Table.HeaderColumn.Title>
			</Table.HeaderColumn>
			<Table.HeaderColumn align="left" usePadding visible={columns['phaseDates.deadline']} width="MEDIUM">
				<Table.HeaderColumn.Title>
					{formatMessage({id: 'common.phase'})} {formatMessage({id: 'common.end_date'})}
				</Table.HeaderColumn.Title>
			</Table.HeaderColumn>
			<Table.HeaderColumn align="left" usePadding visible={columns.sprint} width="MEDIUM">
				<Table.HeaderColumn.Title>{formatMessage({id: 'common.sprint'})}</Table.HeaderColumn.Title>
			</Table.HeaderColumn>
			<Table.HeaderColumn align="left" usePadding visible={columns['sprintDates.startDate']} width="MEDIUM">
				<Table.HeaderColumn.Title>
					{formatMessage({id: 'common.sprint'})} {formatMessage({id: 'common.start_date'})}
				</Table.HeaderColumn.Title>
			</Table.HeaderColumn>
			<Table.HeaderColumn align="left" usePadding visible={columns['sprintDates.deadline']} width="MEDIUM">
				<Table.HeaderColumn.Title>
					{formatMessage({id: 'common.sprint'})} {formatMessage({id: 'common.end_date'})}
				</Table.HeaderColumn.Title>
			</Table.HeaderColumn>
			<Table.HeaderColumn align="right" usePadding visible={columns.progress} width="SMALL">
				<Table.HeaderColumn.Title>{formatMessage({id: 'common.progress'})}</Table.HeaderColumn.Title>
			</Table.HeaderColumn>
			{hasFinancialCategoriesUpdate ? getColumnsWithCategoriesFF(columns) : getColumnsWithoutCategoriesFF(columns)}
			<Table.HeaderColumn align="right" usePadding visible={columns['work.difference.hours']} width="MEDIUM">
				<Table.HeaderColumn.Title>
					{formatMessage({id: 'common.difference'})}
					<br />
					{formatMessage({id: 'common.hours'})}
				</Table.HeaderColumn.Title>
			</Table.HeaderColumn>
			<Table.HeaderColumn align="right" usePadding visible={columns['work.difference.points']} width="MEDIUM">
				<Table.HeaderColumn.Title>
					{formatMessage({id: 'common.difference'})}
					<br />
					{formatMessage({id: 'common.points'})}
				</Table.HeaderColumn.Title>
			</Table.HeaderColumn>
			<Table.HeaderColumn
				align="right"
				usePadding
				visible={columns['financials.timeMaterial.plannedRevenue']}
				width="MEDIUM"
			>
				<Tooltips.EstimatedBillableValueOfServiceTooltip size={'m'}>
					<Table.HeaderColumn.Title>
						{formatMessage({
							id: hasFinancialCategoriesUpdate
								? 'common.estimated_billable_value_of_service'
								: 'project_budget.planned_billable_time',
						})}
					</Table.HeaderColumn.Title>
				</Tooltips.EstimatedBillableValueOfServiceTooltip>
			</Table.HeaderColumn>
			<Table.HeaderColumn
				align="right"
				usePadding
				visible={columns['financials.timeMaterial.actualRevenue']}
				width="MEDIUM"
			>
				<Tooltips.ActualBillableValueOfServiceTooltip size={'m'}>
					<Table.HeaderColumn.Title>
						{formatMessage({
							id: hasFinancialCategoriesUpdate
								? 'common.actual_billable_value_of_service'
								: 'project_budget.actual_billable_time',
						})}
					</Table.HeaderColumn.Title>
				</Tooltips.ActualBillableValueOfServiceTooltip>
			</Table.HeaderColumn>
			<Table.HeaderColumn
				align="right"
				usePadding
				visible={columns['financials.timeMaterial.remainingRevenue']}
				width="MEDIUM"
			>
				<Tooltips.RemainingWorkBillableValueOfServiceTooltip size={'l'}>
					<Table.HeaderColumn.Title>
						{formatMessage({
							id: hasFinancialCategoriesUpdate
								? 'common.remaining_work_billable_value_of_service'
								: 'project_budget.forecast_billable_time_and_expenses',
						})}
					</Table.HeaderColumn.Title>
				</Tooltips.RemainingWorkBillableValueOfServiceTooltip>
			</Table.HeaderColumn>
			<Table.HeaderColumn
				align="right"
				usePadding
				visible={columns['financials.timeMaterial.forecastRevenue']}
				width="MEDIUM"
			>
				<Tooltips.ProjectedTotalBillableValueOfServiceTooltip size={'xl'}>
					<Table.HeaderColumn.Title>
						{formatMessage({
							id: hasFinancialCategoriesUpdate
								? 'common.projected_total_billable_value_of_service'
								: 'project_budget.total_billable_time',
						})}
					</Table.HeaderColumn.Title>
				</Tooltips.ProjectedTotalBillableValueOfServiceTooltip>
			</Table.HeaderColumn>

			<Table.HeaderColumn
				align="right"
				usePadding
				visible={columns['financials.revenueRecognition.plannedRevenue']}
				width="MEDIUM"
			>
				<Tooltips.EstimatedRevenueTooltip size={'m'}>
					<Table.HeaderColumn.Title>
						{formatMessage({
							id: 'common.estimated_revenue',
						})}
					</Table.HeaderColumn.Title>
				</Tooltips.EstimatedRevenueTooltip>
			</Table.HeaderColumn>
			<Table.HeaderColumn
				align="right"
				usePadding
				visible={columns['financials.revenueRecognition.recognitionLockedRevenue']}
				width="MEDIUM"
			>
				<Table.HeaderColumn.Title>
					{formatMessage({id: 'project_budget.revenue_recognition_from_locked_months'})}
				</Table.HeaderColumn.Title>
			</Table.HeaderColumn>
			<Table.HeaderColumn
				align="right"
				usePadding
				visible={columns['financials.revenueRecognition.actualRevenue']}
				width="MEDIUM"
			>
				<Tooltips.ActualRevenueTooltip size={'xl'}>
					<Table.HeaderColumn.Title>{formatMessage({id: 'project_budget.actual_revenue'})}</Table.HeaderColumn.Title>
				</Tooltips.ActualRevenueTooltip>
			</Table.HeaderColumn>
			<Table.HeaderColumn
				align="right"
				usePadding
				visible={columns['financials.revenueRecognition.recognitionOpenRevenue']}
				width="MEDIUM"
			>
				<Table.HeaderColumn.Title>
					{formatMessage({id: 'project_budget.revenue_recognition_from_open_months'})}
				</Table.HeaderColumn.Title>
			</Table.HeaderColumn>
			<Table.HeaderColumn
				align="right"
				usePadding
				visible={columns['financials.revenueRecognition.remainingWorkRevenue']}
				width="MEDIUM"
			>
				<Tooltips.RemainingWorkRevenueTooltip size={'xl'}>
					<Table.HeaderColumn.Title>
						{formatMessage({id: 'project_budget.remaining_work_revenue'})}
					</Table.HeaderColumn.Title>
				</Tooltips.RemainingWorkRevenueTooltip>
			</Table.HeaderColumn>
			<Table.HeaderColumn
				align="right"
				usePadding
				visible={columns['financials.revenueRecognition.recognitionForecastRevenue']}
				width="MEDIUM"
			>
				<Tooltips.ProjectedTotalRevenueTooltip size={'l'}>
					<Table.HeaderColumn.Title>
						{formatMessage({
							id: hasFinancialCategoriesUpdate
								? 'common.projected_total_revenue'
								: 'project_budget.recognition_forecast_revenue',
						})}
					</Table.HeaderColumn.Title>
				</Tooltips.ProjectedTotalRevenueTooltip>
			</Table.HeaderColumn>
			<Table.HeaderColumn
				align="right"
				usePadding
				visible={columns['financials.revenueRecognition.recognitionSurplus']}
				width="MEDIUM"
			>
				<Tooltips.ProjectedOverUnderServicingTooltip size={'xl'}>
					<Table.HeaderColumn.Title>
						{formatMessage({
							id: hasFinancialCategoriesUpdate
								? 'common.projected_over_under_servicing'
								: 'project_budget.variance',
						})}
					</Table.HeaderColumn.Title>
				</Tooltips.ProjectedOverUnderServicingTooltip>
			</Table.HeaderColumn>
			<Table.HeaderColumn align="center" usePadding visible={columns.approved} width="SMALL">
				<Table.HeaderColumn.Title>{formatMessage({id: 'common.approved'})}</Table.HeaderColumn.Title>
			</Table.HeaderColumn>
			<Table.HeaderColumn align="left" usePadding visible={columns.labels} width="EXTRA_LARGE">
				<Table.HeaderColumn.Title>{formatMessage({id: 'common.labels'})}</Table.HeaderColumn.Title>
			</Table.HeaderColumn>
			{customFields.map(customField => (
				<Table.HeaderColumn align="center" usePadding visible={customField.visible} width="MEDIUM">
					<Table.HeaderColumn.Title>{customField.displayName}</Table.HeaderColumn.Title>
				</Table.HeaderColumn>
			))}
		</Table.Header>
	);
};
